import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from './SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLock, FiX, FiArrowRight, FiZap, FiShield, FiTrendingUp } = FiIcons;

// Plan hierarchy for upgrade recommendations
const PLAN_HIERARCHY = ['none', 'starter', 'pro_trader', 'institutional_ai', 'enterprise'];

const PLAN_DETAILS = {
  starter: {
    name: 'Starter',
    price: 'Free',
    features: ['5 AI Analyses/month', 'All asset classes', 'Basic verdict', 'Chart screenshots'],
    color: 'from-green-500 to-emerald-600',
    borderColor: 'border-green-500/30',
  },
  pro_trader: {
    name: 'Pro Trader',
    price: '$49/mo',
    features: ['Unlimited AI Analyses', 'Counterfactual Tracking', 'TDI Score', 'Multi-chart analysis', 'Auto-saved journal'],
    color: 'from-cyan-500 to-blue-600',
    borderColor: 'border-cyan-500/30',
  },
  institutional_ai: {
    name: 'Institutional',
    price: '$149/mo',
    features: ['Full Institutional Review', 'Risk Officer Stance', 'Capital Protection Score', 'Audit PDF Reports', 'Priority Support'],
    color: 'from-purple-500 to-pink-600',
    borderColor: 'border-purple-500/30',
  },
  enterprise: {
    name: 'Enterprise',
    price: 'Custom',
    features: ['Compliance Oversight', 'Audit API', 'Multi-User Dashboards', 'White-Label AI', 'Priority Support'],
    color: 'from-amber-500 to-yellow-600',
    borderColor: 'border-amber-500/30',
  },
};

const UPGRADE_MESSAGES = {
  limit_reached: {
    title: 'Analysis Limit Reached',
    description: "You've used all your AI analyses for this month. Upgrade to Pro Trader for unlimited analyses + full discipline analytics — or come back next month.",
    suggestedPlan: 'pro_trader',
  },
  counterfactual: {
    title: 'Counterfactual Tracking',
    description: 'Track what would have happened if you had traded — prove the value of discipline.',
    suggestedPlan: 'pro_trader',
  },
  tdi_score: {
    title: 'TDI Score',
    description: 'Your personal Trader Discipline Index — a FICO-like score for trading discipline.',
    suggestedPlan: 'pro_trader',
  },
  institutional_review: {
    title: 'Institutional Review Block',
    description: 'Full Risk Officer oversight with Capital Protection Score and Market Alignment.',
    suggestedPlan: 'institutional_ai',
  },
  audit_pdf: {
    title: 'Audit PDF Reports',
    description: 'Generate PM-ready institutional audit reports for compliance and investors.',
    suggestedPlan: 'institutional_ai',
  },
  compliance: {
    title: 'Compliance Features',
    description: 'Enterprise-grade compliance oversight, audit API, and multi-user dashboards.',
    suggestedPlan: 'enterprise',
  },
};

const UpgradeDialog = ({ 
  isOpen, 
  onClose, 
  reason = 'limit_reached',
  currentPlan = 'none',
  customMessage = null
}) => {
  const navigate = useNavigate();
  if (!isOpen) return null;

  const messageConfig = UPGRADE_MESSAGES[reason] || UPGRADE_MESSAGES.limit_reached;
  const suggestedPlan = messageConfig.suggestedPlan;
  const planDetails = PLAN_DETAILS[suggestedPlan];

  const handleViewPlans = () => {
    // /choose-plan is the real upgrade/checkout page (BrowserRouter, no hash).
    navigate('/choose-plan');
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md bg-gray-900 rounded-2xl border border-purple-500/30 shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-purple-600/20 to-cyan-600/20 p-6 border-b border-purple-500/20">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <SafeIcon icon={FiX} className="text-gray-400" />
            </button>
            
            <div className="flex items-center">
              <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/30 mr-4">
                <SafeIcon icon={FiLock} className="text-cyan-400 text-2xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{messageConfig.title}</h2>
                <p className="text-sm text-gray-400">Upgrade to unlock this feature</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-gray-300 mb-6">
              {customMessage || messageConfig.description}
            </p>

            {/* Suggested Plan Card */}
            <div className={`bg-gradient-to-br from-gray-800/80 to-gray-900/50 rounded-xl p-4 border ${planDetails.borderColor} mb-6`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <SafeIcon icon={FiZap} className="text-cyan-400 mr-2" />
                  <span className="font-bold text-white">{planDetails.name}</span>
                </div>
                <span className="text-lg font-bold text-cyan-400">{planDetails.price}</span>
              </div>
              
              <ul className="space-y-2">
                {planDetails.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-sm text-gray-300">
                    <SafeIcon icon={FiTrendingUp} className="text-green-400 mr-2 text-xs" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Current Plan Info */}
            {currentPlan && currentPlan !== 'none' && (
              <p className="text-xs text-gray-500 mb-4 text-center">
                Your current plan: <span className="text-gray-400 font-medium capitalize">{currentPlan.replace('_', ' ')}</span>
              </p>
            )}

            {/* CTA Buttons */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-3 px-4 rounded-xl border border-gray-700 text-gray-400 hover:bg-gray-800 transition-colors"
              >
                Maybe Later
              </button>
              <button
                onClick={handleViewPlans}
                className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white font-semibold flex items-center justify-center transition-all"
              >
                View Plans
                <SafeIcon icon={FiArrowRight} className="ml-2" />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UpgradeDialog;
